export default {
    "welcome": {
        "title": "Welcome to ProcrastiQ",
        "subtitle": "Your productivity companion",
        "modal": {
            "title": "Welcome to ProcrastiQ",
            "headline": "Smart breaks for your brain",
            "subheading": "Brain snacks, facts and mindful moments that turn spare minutes into something meaningful.",
            "text": "Procrastiq is where procrastination becomes something meaningful. Instead of endlessly scrolling through social media, you'll discover quick brain snacks designed to entertain, stimulate your mind, and maybe even teach you something new.\n\nThis is not about productivity at all costs. Everyone needs a moment to pause, recharge, and step away from work. Procrastiq helps you turn those small breaks into something positive — filled with curiosity, enjoyment, and everyday brain training. Small challenges. Big progress.",
            "cta": "Let's go"
        }
    },
    "login": {
        "title": "Login",
        "email": "Email",
        "password": "Password",
        "submit": "Login",
        "cancel": "Cancel",
        "success": "Login successful",
        "lead": "Welcome back — pick up your streak and climb the leagues."
    },
    "auth": {
        "bot_required": "Please complete the security check."
    },
    "logout": "Logout",
    "logo": {
        "title": "ProcrastiQ",
        "slogan": "Smart breaks for your brain"
    },
    "register": {
        "title": "Register",
        "name": "Name",
        "email": "Email",
        "password": "Password",
        "submit": "Create account",
        "cancel": "Cancel",
        "success": "Registration successful",
        "country": "Country",
        "lead": "Create an account to earn XP, unlock ranks, and join weekly leagues."
    },
    "social": {
        "divider": "Or continue with",
        "signInWith": "Sign in with {{provider}}",
        "continueWith": "Continue with {{provider}}",
        "failed": "Social sign-in failed",
        "popup_blocked": "Popup was blocked. Please allow popups and try again.",
        "cancelled": "Sign-in was cancelled."
    },
    "common": {
        "cancel": "Cancel",
        "saving": "Saving...",
        "unknown_error": "Unknown error",
        "select_country": "Select country",
        "select_option": "Select an option",
        "search": "Search…",
        "no_options": "No options",
        "country": "Country"
    },
    "language": {
        "page_language": "Page language",
        "switch_to": "Switch to {{language}}"
    },
    "cookies": {
        "title": "Cookies",
        "message": "We use cookies for analytics (Google Analytics) so we can improve Procrastiq.",
        "privacy_link": "Privacy policy",
        "accept": "Accept",
        "reject": "Reject"
    },
    "footer": {
        "columns": {
            "about": {
                "title": "About us",
                "mission": "Mission",
                "progression": "XP, levels & leagues",
                "creator_progression": "Creator XP & ranks",
                "contact": "Contact us"
            },
            "leaderboards": {
                "title": "Leaderboards",
                "players": "Members",
                "creators": "Contributors"
            },
            "legal": {
                "title": "Privacy & Terms",
                "community": "Community guidelines",
                "terms": "Terms of service",
                "privacy": "Privacy policy"
            },
            "social": {
                "title": "Social",
                "facebook": "Facebook",
                "instagram": "Instagram"
            }
        }
    },
    "pages": {
        "mission": {
            "title": "Mission",
            "content": "ProcrastiQ turns small breaks into meaningful moments — brain snacks, puzzles, and mindful pauses that entertain and stimulate your mind.\n\nWe believe procrastination does not have to be wasted time. Everyone deserves a moment to recharge, and we help make those moments positive, curious, and fun."
        },
        "contact": {
            "title": "Contact us",
            "content": "Have a question, an idea for improvement, or did you run into something that doesn’t work the way it should? Get in touch — every piece of feedback helps us make Procrastiq better.\n\nYou can write to us at:\n\nhello@procrastiq.com\n\nWe’d also love to hear your ideas for new content, features, or topics you’d like to see on Procrastiq."
        },
        "community": {
            "title": "Community guidelines",
            "content": "ProcrastiQ is a welcoming space for curious minds. Be respectful, kind, and constructive in your interactions.\n\nDo not post harmful, abusive, or spam content. Report anything that does not belong. Together we keep this a positive place to learn and take smart breaks."
        },
        "terms": {
            "title": "Terms of service",
            "content": "**Last updated: 7 September 2026**\n\nBy using the Procrastiq service, you agree to these terms. If you do not agree, please do not use the service.\n\n## 1. About Procrastiq\n\nProcrastiq is an online platform focused on learning, quizzes, puzzles, and other interactive content.\n\nThe service content is intended primarily for education and entertainment. It is not professional, legal, medical, financial, or other specialist advice.\n\n## 2. User account\n\nSome Procrastiq features may require creating a user account.\n\nYou are responsible for the accuracy of the information in your account and for keeping your login credentials secure.\n\nAn account must not be used to violate the rights of others, applicable laws, or these terms.\n\n## 3. Rules of use\n\nWhen using Procrastiq, you must not, in particular:\n\n* publish illegal, fraudulent, hateful, or otherwise harmful content,\n* infringe copyright or other third-party rights,\n* harass other users,\n* attempt to disrupt the security or operation of the service,\n* scrape content or data in ways we do not allow,\n* abuse ratings, XP, leaderboards, or other game mechanics.\n\n## 4. User content\n\nUsers may be able to create or submit their own content, such as quizzes, questions, comments, or other contributions.\n\nBy submitting content, you confirm that you have the right to publish it.\n\nYou retain your rights to your content. At the same time, you grant us a non-exclusive right to store, display, technically adapt, and make that content available within Procrastiq as needed to operate the service.\n\n## 5. Content moderation\n\nWe may review user content and, if these terms or applicable law are violated, refuse, hide, or remove it.\n\nIn serious or repeated cases, we may restrict or terminate a user account.\n\nWhere possible under the circumstances and required by law, we will inform the user of the reason for such a decision.\n\n## 6. Procrastiq content\n\nContent published directly by Procrastiq may contain errors or inaccuracies. Although we aim to check and continuously improve information, we cannot guarantee completeness or freedom from mistakes.\n\nQuizzes and other educational content are primarily informational and recreational.\n\n## 7. Service availability\n\nWe aim to keep Procrastiq available and working, but we cannot guarantee uninterrupted or error-free operation.\n\nWe may change, expand, or discontinue parts of the service over time.\n\n## 8. Liability\n\nTo the extent permitted by applicable law, we are not liable for damage arising from improper use of the service or reliance on content that is informational or educational only.\n\nThis does not affect user rights that cannot be limited under applicable law.\n\n## 9. Privacy\n\nInformation about how we process personal data is available in our [Privacy policy](/legal/privacy).\n\n## 10. Changes to these terms\n\nWe may update these terms from time to time, for example because of changes to the service, the law, or how Procrastiq works.\n\nThe date of the latest update will always appear at the top of this page. We may also notify users of significant changes through the service or by email.\n\n## 11. Contact\n\nIf you have questions about these terms, you can contact us at:\n\n[hello@procrastiq.com](mailto:hello@procrastiq.com)"
        },
        "privacy": {
            "title": "Privacy policy",
            "content": "**Last updated: 7 September 2026**\n\nWe take your privacy seriously. Below we explain what personal data we process when you use Procrastiq, why we need it, and what rights you have.\n\n## 1. Who processes the data\n\nThe controller of personal data is:\n\n**Michal Orlík**\nnatural person\n\nContact for questions about personal data protection:\n\n**[hello@procrastiq.com](mailto:hello@procrastiq.com)**\n\n## 2. What data we process\n\nDepending on how you use Procrastiq, we may process in particular:\n\n* account details, such as name, username, and email address,\n* data obtained when signing in through an external service, such as Google,\n* information about use of the service, such as viewed content, quiz results, XP earned, streaks, reactions, bookmarks, and feed activity,\n* content you create or submit yourself,\n* technical data, such as IP address, device type, browser, and information needed for security and proper operation of the service.\n\n## 3. Why we use the data\n\nWe use personal data mainly so that we can:\n\n* create and manage your account,\n* provide Procrastiq features,\n* store your progress, results, XP, and other activity,\n* personalize content and recommendations,\n* protect the service from misuse,\n* resolve errors and technical issues,\n* communicate with you,\n* analyze and improve how the service works.\n\n## 4. Legal basis for processing\n\nWe process data for each purpose mainly:\n\n* because it is necessary to provide the service,\n* based on our legitimate interest in securing and improving Procrastiq,\n* based on your consent where consent is required,\n* or to comply with legal obligations.\n\nIf processing is based on consent, you may withdraw it at any time.\n\n## 5. Who data may be shared with\n\nWe do not sell your personal data.\n\nTo operate the service, however, we may use external providers, for example for:\n\n* hosting and infrastructure,\n* user authentication,\n* sending emails and notifications,\n* analytics and error diagnostics.\n\nThese providers may process data only to the extent needed to provide their services.\n\n## 6. How long we keep the data\n\nData linked to your account is generally kept for as long as the account exists and then for as long as needed to meet legal obligations or protect our legitimate interests.\n\nTechnical and analytics data is kept only for as long as needed for its purpose.\n\n## 7. Your rights\n\nIn connection with the processing of personal data, you may, depending on the circumstances, have the right to:\n\n* obtain information about what data we process about you,\n* request correction of inaccurate data,\n* request erasure of data,\n* request restriction of processing,\n* object to certain forms of processing,\n* receive your data in a portable format,\n* withdraw consent you have given.\n\nYou can send a request to:\n\n**[hello@procrastiq.com](mailto:hello@procrastiq.com)**\n\nWe respond to requests in accordance with applicable law.\n\n## 8. Account deletion\n\nIf the service allows account deletion, you can request removal in your account settings or contact us at **[hello@procrastiq.com](mailto:hello@procrastiq.com)**.\n\nWhen an account is deleted, we remove or anonymize personal data that we no longer need to keep for legal or security reasons.\n\n## 9. Cookies and similar technologies\n\nProcrastiq may use cookies and similar technologies needed for sign-in, security, storing settings, and optionally analytics.\n\nMore detailed information about cookies may be provided in a separate cookie policy.\n\n## 10. Security\n\nWe use appropriate technical and organizational measures to protect personal data against unauthorized access, loss, or misuse.\n\nNo system can be considered absolutely secure, however.\n\n## 11. Complaints\n\nIf you believe we handle your personal data in breach of applicable law, you may also contact the competent supervisory authority.\n\nIn the Czech Republic, that is the **Office for Personal Data Protection (Úřad pro ochranu osobních údajů)**.\n\n## 12. Changes to this policy\n\nWe may update this policy from time to time, for example in connection with new features or changes in law.\n\nWe will always show the date of the latest update at the top of this page."
        },
        "progression": {
            "title": "XP, levels & leagues",
            "content": "ProcrastiQ rewards curiosity. Answer quizzes, build streaks, and climb from casual brain snacks to weekly competition.\n\n## Experience points (XP)\n\nYou earn permanent XP when you play well — for example correct answers, first-try bonuses, harder questions, reviews, and daily goals. Permanent XP never resets and always counts toward your level.\n\nThere is also ranked XP used only for weekly leagues. Ranked XP uses daily diminishing returns: early XP in the day counts fully, later gains count less. That keeps competition fair if someone plays all day.\n\n## Levels\n\nYour level comes from total permanent XP. Each level needs a bit more XP than the last (a growing curve), so progress stays meaningful.\n\nYour profile shows how much XP you have in the current level and how much you need for the next one.\n\n## Title ranks\n\nRanks are titles unlocked by level. They are a badge of how far you have come — separate from weekly league tiers.\n\n- Level 1 — Curious Mind\n- Level 3 — Explorer\n- Level 5 — Researcher\n- Level 8 — Problem Solver\n- Level 12 — Analyst\n- Level 18 — Logician\n- Level 25 — Strategist\n- Level 35 — Polymath\n- Level 50 — Genius\n- Level 70 — Master of Knowledge\n- Level 100 — Procrastiq Legend\n\n## Weekly leagues\n\nLeagues are a weekly competition with other players in groups of about 20.\n\nTiers from lowest to highest:\n\n- Iron\n- Bronze\n- Silver\n- Gold\n- Platinum\n- Diamond\n- Master\n- Legendary\n\nEach week you compete on ranked XP inside your group. At the end of the week, roughly the top players promote one tier and the bottom players relegate one tier. Everyone else stays.\n\nYou do not wait for a Monday reset to join. When you become eligible, you are placed into an open group right away — or a new group is created for you.\n\nTo unlock leagues you generally need:\n\n- at least 100 permanent XP\n- at least 10 completed activities\n- activity on at least 2 different days\n- leaderboards visibility turned on for your profile\n\nOpen the Leaderboards page or your stats panel anytime to see your tier, position, and group."
        },
        "creator_progression": {
            "title": "Creator XP & ranks",
            "content": "Creating content on ProcrastiQ has its own progression — completely separate from player XP, levels, and weekly leagues.\n\nWhen people enjoy your quizzes and brain snacks, you earn Creator XP (CXP). CXP unlocks creator ranks and places you on the Contributors leaderboards.\n\n## Two independent systems\n\n- Player progression — XP from playing, levels, title ranks, weekly leagues\n- Creator progression — CXP from contributing quality content, creator ranks, Contributors boards\n\nPlaying does not raise your creator rank. Creating does not raise your player level. You can grow in both tracks at once.\n\n## How you earn Creator XP\n\nCXP is awarded for approved contributions and for engagement on that content — not for how many drafts you submit.\n\nTypical rewards:\n\n- Approved content — +20 CXP (once per item)\n- Every 10 completions on your content — +2 CXP\n- Every 10 likes on your content — +5 CXP\n- Every 10 bookmarks on your content — +2 CXP\n\nViews and other stats are tracked on your creator profile, but only the rewards above grant CXP. Values may be tuned over time.\n\n## Creator ranks\n\nYour creator rank comes from total CXP. Thresholds grow roughly exponentially so early progress feels quick and higher ranks stay meaningful.\n\n- 0 CXP — Contributor\n- 50 CXP — Creator\n- 150 CXP — Author\n- 400 CXP — Expert\n- 1,000 CXP — Mentor\n- 2,500 CXP — Master Creator\n- 6,000 CXP — Procrastiq Sage\n\nYour profile shows your current rank, CXP, and progress toward the next rank.\n\n## Contributors leaderboards\n\nCreators are ranked by Creator XP, not by number of submissions.\n\n- Weekly — CXP earned this week\n- Monthly — CXP earned this month\n- All Time — total CXP\n\nOpen Leaderboards and switch to Contributors to see who is climbing."
        }
    },
    "account": {
        "profile_picture": "Profile Picture",
        "save_changes": "Save Changes",
        "update_success": "Profile updated successfully",
        "update_failed": "Failed to update profile",
        "country": "Country",
        "avatar": {
            "upload_label": "Upload Avatar",
            "upload_success": "Avatar uploaded successfully",
            "upload_failed": "Failed to upload avatar",
            "delete_confirm": "Are you sure you want to delete your avatar?",
            "delete_success": "Avatar deleted successfully",
            "delete_failed": "Failed to delete avatar"
        },
        "password": {
            "set_optional": "Set Password (Optional)",
            "change_optional": "Change Password (Optional)",
            "oauth_info": "You signed in with {{method}}. You can optionally set a password to enable email/password login.",
            "current": "Current Password",
            "current_placeholder": "Enter current password",
            "current_required": "Current password is required to change password",
            "new": "New Password",
            "set_placeholder": "Set a password",
            "leave_blank_placeholder": "Leave blank to keep current",
            "confirm": "Confirm New Password",
            "confirm_placeholder": "Confirm new password",
            "mismatch": "New passwords do not match"
        }
    },
    "play": {
        "label": "Play",
        "games": {
            "memory": "Memory",
            "focus": "Focus",
            "sequence": "Sequence",
            "reflex": "Reflex"
        },
        "memory": {
            "hint_preview": "Remember the highlighted squares.",
            "hint_input": "Tap the squares you remember.",
            "cell": "Square {{n}}"
        },
        "focus": {
            "hint": "Find the different symbol."
        },
        "sequence": {
            "title": "Remember the sequence",
            "countdown_hint": "Get ready — the sequence is about to start.",
            "go": "Go!",
            "hint_watch": "Watch carefully.",
            "hint_input": "Now repeat the sequence."
        },
        "reflex": {
            "hint": "Choose the color, not the word.",
            "progress": "{{current}} / {{total}}"
        },
        "colors": {
            "red": "RED",
            "blue": "BLUE",
            "green": "GREEN",
            "orange": "ORANGE"
        },
        "result": {
            "success": "Nice!",
            "fail": "✗ Wrong."
        }
    },
    "pause": {
        "label": "Pause",
        "types": {
            "breathing": "Breathing",
            "eyes": "Eyes break",
            "stretch": "Quick stretch",
            "grounding": "Reset"
        },
        "skip": "Skip",
        "continue": "Continue",
        "breathing": {
            "inhale": "Breathe in",
            "exhale": "Breathe out"
        },
        "eyes": {
            "step_far": "Look at something farther away from the screen.",
            "step_blink": "Relax your eyes and blink slowly several times.",
            "step_close": "Close your eyes for a moment."
        },
        "stretch": {
            "step_shoulders": "Lower your shoulders and let them relax.",
            "step_sit": "Sit a little taller and relax your back.",
            "step_roll": "Slowly roll your shoulders."
        },
        "grounding": {
            "prompt_color": "Find 3 things around you that have the same color.",
            "prompt_sounds": "Notice 2 different sounds around you.",
            "prompt_look_away": "Look away from the screen for a few seconds.",
            "prompt_texture": "Touch something nearby and notice its texture.",
            "prompt_breath": "Take one slow breath and feel your feet on the floor.",
            "prompt_feet": "Press your feet gently into the ground and notice the contact."
        },
        "result": {
            "done": "Done"
        }
    },
    "content": {
        "loading": "Loading...",
        "no_content": "No content yet.",
        "feed_exhausted_tag": "You have already answered everything for this tag",
        "feed_exhausted_category": "You have already answered everything for this category",
        "browse_feed": "Browse content feed",
        "back_to_feed": "← Back to feed",
        "back": "← Back",
        "content_not_found": "Content not found",
        "failed_load": "Failed to load content",
        "failed_load_more": "Failed to load more",
        "list": {
            "showing": "Showing {{count}} of {{total}}",
            "load_more": "Load more"
        },
        "answer": {
            "your_answer": "Your answer...",
            "your_result_or_answer": "Your result or answer...",
            "select_or_type": "Select your answer...",
            "fill_in_blank": "Fill in the blank...",
            "your_answer_flip": "Your answer (or flip to check)...",
            "reveal_answer": "Reveal answer",
            "self_check_prompt": "Did you know it?",
            "knew_it": "I knew it",
            "need_practice": "Need more practice",
            "check": "Check",
            "completed": "Completed",
            "completing": "Saving...",
            "skip": "Skip",
            "next": "Next",
            "checking": "Checking...",
            "correct": "✓ Correct!",
            "not_quite": "✗ Not quite.",
            "correct_answer": "Correct answer: {{answer}}"
        },
        "stats": {
            "sign_in_to_react": "Sign in to like or dislike",
            "bookmark": "Bookmark",
            "remove_bookmark": "Remove bookmark",
            "save_for_later": "Save for later",
            "saved": "Saved",
            "share": "Share",
            "link_copied": "Link copied to clipboard",
            "share_failed": "Could not share this quiz",
            "completions": "Completions: {{count}}",
            "aria_like": "Like",
            "aria_dislike": "Dislike"
        },
        "admin": {
            "edit": "Edit",
            "delete": "Delete",
            "stats": "Stats",
            "stats_title": "Content stats",
            "delete_confirm": "Delete \"{{title}}\"?",
            "deleted": "Content deleted",
            "delete_failed": "Failed to delete content",
            "load_failed": "Failed to load content",
            "id": "ID",
            "slug": "Slug",
            "status": "Status",
            "locale": "Locale",
            "likes": "Likes",
            "dislikes": "Dislikes",
            "completions": "Completions",
            "open_in_admin": "Open in admin"
        },
        "difficulty": {
            "label": "Difficulty: {{value}}/{{max}}",
            "value": "{{value}}/{{max}}",
            "aria": "Difficulty {{value}} out of {{max}}"
        },
        "detail": {
            "sign_in_to_answer": "Sign in to answer this question.",
            "duration": "Duration: {{minutes}} min",
            "phase": "Phase {{number}}"
        },
        "list_item": {
            "completions": "{{count}} completions",
            "completions_label": "Completions: {{count}}"
        },
        "feed": {
            "success": "Correct! 🎉",
            "skip": "Skip",
            "next": "Next",
            "intro_gate": {
                "title": "Sign in to continue",
                "body": "Sign in or create an account and Procrastiq will start adapting to what you enjoy.",
                "body_more": "It won’t uselessly repeat the same questions, it will remember your results, and it will offer content based on your interests.",
                "perks": "You’ll also get access to XP, leagues, Survival, challenges with other players, and more.",
                "register": "Create account",
                "login": "Sign in"
            }
        }
    },
    "categories": {
        "title": "Categories",
        "filter": "Filter by category",
        "all": "All",
        "loading": "Loading categories...",
        "empty": "No categories",
        "expand": "Expand subcategories",
        "collapse": "Collapse subcategories"
    },
    "tags": {
        "title": "Tags",
        "all": "All",
        "loading": "Loading tags...",
        "empty": "No tags"
    },
    "bookmarks": {
        "title": "Saved bookmarks",
        "empty": "No saved content yet."
    },
    "contribute": {
        "title": "Contribute",
        "subtitle": "Submit quizzes for review. Once approved, they appear in the feed.",
        "subtitle_reliable": "You are a reliable contributor — your quizzes go live immediately.",
        "add": "+ Submit quiz",
        "add_first": "Submit your first quiz",
        "empty": "You have not submitted any content yet.",
        "load_failed": "Failed to load your content",
        "cannot_edit_approved": "Approved content can only be changed by an admin.",
        "delete_confirm": "Delete \"{{title}}\"?",
        "deleted": "Content deleted",
        "delete_failed": "Failed to delete content",
        "edit": "Edit",
        "delete": "Delete",
        "form": {
            "submit_title": "Submit a quiz",
            "edit_title": "Edit your quiz",
            "submit_button": "Submit for review",
            "save_button": "Save changes",
            "hint": "Your quiz will be reviewed before it appears in the feed. Reliable contributors are published immediately.",
            "success_updated": "Content updated successfully",
            "success_published": "Content published successfully",
            "success_submitted": "Content submitted for review",
            "success_created": "Content created successfully",
            "success_translation": "Translation created successfully",
            "failed": "Failed to {{action}} content",
            "action_update": "update",
            "action_create": "create",
            "choose_translation_language": "Choose a language for the translation.",
            "admin_add": "Add Content",
            "admin_edit": "Edit Content",
            "admin_add_translation": "Add translation",
            "admin_add_translation_lang": "Add {{lang}} translation",
            "admin_create": "Create Content",
            "admin_update": "Update Content",
            "admin_create_translation": "Create translation"
        }
    },
    "content_form": {
        "title": "Title",
        "question": "Question",
        "question_placeholder": "Write the full quiz question",
        "slug": "Slug",
        "language": "Language",
        "difficulty": "Difficulty (1–5)",
        "country": "Country",
        "status": "Status",
        "visibility": "Visibility",
        "categories": "Categories",
        "category": "Category",
        "choose_category": "Choose category",
        "tags": "Tags",
        "translations": "Translations",
        "translation_new": "(new)",
        "translate_hint": "Translate the question, options, and explanation. Metadata (difficulty, categories, tags) is copied from the source.",
        "choose_language": "Choose language",
        "not_set": "Not set",
        "options": "Options",
        "option_n": "Option {{n}}",
        "correct_answer": "Correct answer",
        "explanation": "Explanation",
        "explanation_placeholder": "Optional — shown after a wrong answer"
    },
    "user_stats": {
        "title": "Your Stats",
        "sign_in_prompt": "Sign in to see your stats",
        "guest_title": "Rank. Level up. Climb leagues.",
        "guest_lead": "Track XP, unlock title ranks, and compete in weekly leagues once you sign in.",
        "guest_perk_level": "Earn XP from quizzes and rise through levels.",
        "guest_perk_rank": "Unlock title ranks as your level grows.",
        "guest_perk_league": "Join weekly groups and promote toward Legendary.",
        "xp": "XP",
        "streak": "Streak",
        "best_streak": "Best streak",
        "solved": "Answered",
        "correct": "Correct",
        "accuracy": "Accuracy",
        "bookmarks": "Bookmarks",
        "contribute": "Contribute",
        "likes": "Likes",
        "language_mastered": "Mastered",
        "language_learning": "Learning",
        "last_7_days": "Last 7 days",
        "days_suffix": "d",
        "no_activity": "No activity yet",
        "weekly_league": "Weekly league",
        "league_tiers": "League tiers",
        "league_tiers_hint": "Tiers from lowest to highest. Finish top of your group to promote; bottom relegates.",
        "league_position": "#{{position}} of {{total}} this week",
        "view_leaderboard": "Full board",
        "how_leagues_work": "How it works",
        "you": "You"
    },
    "gamification": {
        "level": "Level",
        "rank": "Rank",
        "streak": "Streak",
        "daily_goals": "Daily goals",
        "achievements": "Achievements",
        "leaderboard": "Leaderboard",
        "season": "Season",
        "xp_to_next": "{{current}} / {{next}} XP to next level",
        "weekly": "Weekly",
        "monthly": "This month",
        "overall": "Overall",
        "monthly_hint": "Ranks players by ranked XP earned during the current calendar-month season (same diminishing returns as weekly leagues). Not a separate league tier.",
        "overall_hint": "All-time ranking by permanent total XP.",
        "overall_empty": "No players on the overall board yet.",
        "players": "members",
        "full_board": "Full board",
        "player": "Member",
        "ranked_xp": "Ranked XP",
        "total_xp": "Total XP",
        "standing": "Standing",
        "zone": "Standing",
        "zone_promotion": "Promotion",
        "zone_safe": "Safe",
        "zone_relegation": "Relegation",
        "zone_legend": "Top {{promo}} promote · bottom {{releg}} relegate at week end.",
        "unlocked": "Unlocked",
        "achievements_error": "Failed to load achievements",
        "achievements_intro": "Unlock badges as you play — activity, accuracy, streaks, difficulty, and learning from mistakes.",
        "achievement_categories": {
            "activity": "Activity",
            "correct_answers": "Correct answers",
            "learning_from_mistakes": "Learning from mistakes",
            "streaks": "Streaks",
            "difficulty": "Difficulty",
            "variety": "Variety",
            "general": "General"
        },
        "achievement_category_hints": {
            "learning_from_mistakes": "After a wrong answer, that quiz can return later for review. Answering it correctly then counts here."
        },
        "your_rank": "Your rank",
        "around_you": "Around you",
        "tooltip_total_xp": "{{xp}} total XP",
        "tooltip_level": "Level {{level}}",
        "weekly_waiting": "Unable to load your weekly group. Please try again.",
        "weekly_ineligible": "Keep playing to unlock weekly leagues.",
        "achievements_progress": "{{unlocked}} / {{total}} unlocked",
        "toast": {
            "xp_gained": "+{{xp}} XP",
            "achievement": "Achievement unlocked: {{name}}"
        },
        "level_up": {
            "title": "Level up!",
            "body": "You reached level {{level}}",
            "continue": "Keep going"
        }
    },
    "creator": {
        "rank": "Creator Rank",
        "xp": "Creator XP",
        "tooltip_rank": "{{rank}}",
        "tooltip_cxp": "{{xp}} CXP",
        "approved": "Approved contributions",
        "views": "Views",
        "completions": "Completions",
        "likes": "Likes",
        "bookmarks": "Bookmarks",
        "top_contribution": "Top contribution",
        "newest_contribution": "Newest contribution",
        "to_next": "to next rank",
        "players_board": "Members",
        "creators_board": "Contributors",
        "board_group": "Board type",
        "weekly": "Weekly",
        "monthly": "Monthly",
        "all_time": "All Time",
        "weekly_hint": "Creators ranked by Creator XP earned this week.",
        "monthly_hint": "Creators ranked by Creator XP earned this month.",
        "all_time_hint": "All-time ranking by Creator XP.",
        "empty": "No creators on the board yet."
    },
    "profile": {
        "title": "My Profile",
        "edit": {
            "title": "Edit Profile"
        },
        "nav": {
            "label": "Account",
            "overview": "Overview",
            "stats": "Statistics",
            "leagues": "Leagues",
            "edit": "Edit profile",
            "contribute": "Contribute",
            "bookmarks": "Bookmarks",
            "achievements": "Achievements",
            "leaderboards": "Leaderboards",
            "browse": "Browse content"
        },
        "actions": {
            "back_to_home": "Back to Home",
            "back_to_profile": "Back to Profile",
            "edit": "Edit Profile",
            "contribute": "Contribute"
        },
        "labels": {
            "initials": "Initials",
            "role": "Role",
            "status": "Status",
            "sign_in_method": "Sign-in Method",
            "member_since": "Member Since",
            "country": "Country",
            "reliable": "Reliable contributor"
        },
        "sections": {
            "account_info": "Account Information",
            "authentication": "Authentication",
            "creator": "Creator",
            "stats": "Your stats",
            "leagues": "Weekly leagues"
        },
        "stats": {
            "title": "Statistics",
            "subtitle": "Your progress, activity, and weekly league at a glance.",
            "empty": "No stats yet. Play a few quizzes to get started.",
            "periods": "Today & this week",
            "today": "Today",
            "see_all": "All statistics",
            "see_leagues": "Open leagues page"
        },
        "leagues": {
            "title": "Leagues",
            "subtitle": "Your weekly league tier, group standing, and promotion or relegation zone.",
            "your_group": "Your group this week",
            "see_all": "Open leagues"
        }
    },
    "notifications": {
        "title": "Notifications",
        "hint": "Get alerts about your streak, league position, and your content — even when Procrastiq is closed.",
        "enable": "Enable notifications",
        "disable": "Disable on this device",
        "enabled": "Notifications enabled",
        "disabled": "Notifications disabled on this device",
        "enable_failed": "Could not enable notifications",
        "disable_failed": "Could not disable notifications",
        "prefs_failed": "Could not save preferences",
        "unsupported": "Push notifications are not supported in this browser.",
        "pref_ranking": "League & ranking",
        "pref_streak": "Streak",
        "pref_reactions": "Reactions to my content",
        "pref_approved": "Content approval"
    },
    "games": {
        "challenge_friend": "Challenge a friend",
        "survival_mode": "Survival",
        "challenges_inbox": "Challenges",
        "view_inbox": "View inbox",
        "pick_friend": "Pick a friend",
        "random_opponent": "Random opponent",
        "search_users": "Search by name…",
        "search_error": "Could not search users",
        "selected": "Selected",
        "selected_opponent": "Selected player",
        "pick_opponent": "Pick an opponent first",
        "random_hint": "We'll match you with an active player.",
        "any_category": "Any category",
        "category_optional": "Category (optional)",
        "start_challenge": "Send challenge",
        "challenge_created": "Challenge sent!",
        "create_error": "Could not create challenge",
        "challenge": "Challenge",
        "open": "Open",
        "new_challenge": "New challenge",
        "start_playing": "Start playing",
        "accept_play": "Accept & play",
        "decline": "Decline",
        "decline_error": "Could not decline challenge",
        "your_result": "Your result",
        "opponent_result": "Opponent",
        "waiting_opponent": "Waiting for your opponent to finish…",
        "score_line": "{{correct}}/{{total}} correct · {{time}}",
        "you_won": "You won!",
        "you_lost": "You lost",
        "draw": "It's a draw",
        "submitting": "Submitting answers…",
        "submit_error": "Could not submit challenge",
        "load_error": "Could not load",
        "not_found": "Challenge not found",
        "no_challenges": "No challenges yet",
        "incoming": "Incoming",
        "outgoing": "Outgoing",
        "player": "Player",
        "filter": {
            "incoming": "Incoming",
            "outgoing": "Outgoing",
            "all": "All"
        },
        "status": {
            "pending": "Pending",
            "in_progress": "In progress",
            "completed": "Completed",
            "declined": "Declined",
            "expired": "Expired"
        },
        "question_progress": "Question {{current}} / {{total}}",
        "time_left": "{{seconds}}s",
        "elapsed_time": "Time {{time}}",
        "overall_time": "Total time",
        "total_time_left": "Total {{seconds}}s",
        "no_options": "No answer options for this question",
        "survival_leaderboard": "Survival leaderboard",
        "survival_leaderboard_hint": "Best survival run per player in the selected period.",
        "survival_empty": "No survival scores yet — be the first!",
        "play_survival": "Play Survival",
        "current_score": "Score: {{score}}",
        "game_over": "Game over",
        "final_score": "You scored {{score}}",
        "personal_best": "Personal best: {{score}}",
        "daily_rank": "Daily rank: #{{rank}}",
        "weekly_rank": "Weekly rank: #{{rank}}",
        "play_again": "Play again",
        "survival_start_error": "Could not start survival run",
        "survival_error": "Survival error",
        "daily": "Daily",
        "weekly": "Weekly",
        "all_time": "All time",
        "score": "Score",
        "challenge_user": "Challenge {{name}}",
        "login_to_challenge": "Log in to challenge this player",
        "profile_error": "Could not load profile",
        "profile_not_found": "Player not found",
        "profile": "Profile",
        "notifications": "Notifications",
        "mark_all_read": "Mark all read",
        "no_notifications": "No notifications",
        "blocked_users": "Blocked players",
        "blocked_hint": "Blocked players cannot challenge you.",
        "no_blocked": "You haven't blocked anyone.",
        "unblock": "Unblock",
        "unblocked": "Player unblocked",
        "unblock_error": "Could not unblock player",
        "blocked_load_error": "Could not load blocked players",
        "survival_howto": "Answer correctly for as long as you can. You have 20 seconds per question. One wrong answer is forgiven — a second mistake ends the run. Your score is the number of correct answers.",
        "challenge_howto": "Ten questions, 20 seconds each. More correct answers and a faster time win. You'll see your opponent's result once you have both finished.",
        "continue": "Continue",
        "survival_reserve": "Spare: {{count}}",
        "survival_second_chance": "Second chance!",
        "guest_teaser": "Log in to challenge friends and climb Survival ranks."
    }
};
